/**
 * Formatação de competência e leitura de valor digitado.
 *
 * **Moeda e percentual não estão aqui**: `formatarMoeda` e `formatarPercentual`
 * são da biblioteca de UI (`componentes/ui/ValorFiscal`, do Agente 5), e as telas
 * importam de lá. Manter uma segunda implementação de formato fiscal no projeto
 * é o começo exato do RN-078 — dois lugares formatando o mesmo campo, um deles
 * na escala errada, e o contribuinte recebendo um percentual 100× maior.
 *
 * Formatar **não é calcular**: nada aqui arredonda para decidir um valor
 * gravado. O número exibido é o que veio do backend, que já passou pelo
 * `decimal.js` com `ROUND_HALF_UP` explícito (`docs/arquitetura.md`).
 */

const MESES = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
] as const;

/**
 * `2025-03` → `mar/2025`.
 *
 * A competência viaja como par (ano, mês) em texto, nunca como `Date`: é o que
 * elimina a classe de bug do `converterData` legado (D-001, RN-012), em que
 * meia-noite UTC exibida em BRT virava o mês anterior.
 */
export function formatarCompetencia(competencia: string): string {
  const [ano, mes] = competencia.split('-');
  if (ano === undefined || mes === undefined) return competencia;

  const indice = Number(mes) - 1;
  const nome = MESES[indice];
  return nome === undefined ? competencia : `${nome}/${ano}`;
}

/** Converte o texto digitado (`1.234,56` ou `1234.56`) em número, ou `null`. */
export function lerValorDigitado(texto: string): number | null {
  const limpo = texto.trim().replace(/\s/g, '').replace(/R\$/i, '');
  if (limpo === '') return null;

  // `1.234,56` (pt-BR) e `1234.56` (digitação direta) precisam funcionar: quem
  // opera colando de planilha usa o primeiro, quem digita rápido usa o segundo.
  const normalizado = limpo.includes(',') ? limpo.replace(/\./g, '').replace(',', '.') : limpo;

  const numero = Number(normalizado);
  return Number.isFinite(numero) ? numero : null;
}

/**
 * CNPJ em dígitos → `00.000.000/0000-00`.
 *
 * O banco guarda só dígitos (é a chave natural da empresa e o que casa com o
 * registro 0000 do SPED); a máscara é assunto de tela. Um valor que não tenha 14
 * dígitos volta como veio — melhor mostrar o dado bruto do que uma máscara
 * aplicada sobre algo que não é CNPJ.
 */
export function formatarCnpj(cnpj: string): string {
  const d = cnpj.replace(/\D/g, '');
  if (d.length !== 14) return cnpj;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

/**
 * CPF em dígitos → `000.000.000-00`.
 *
 * Mesma regra do CNPJ: o que não tiver 11 dígitos volta como veio. Aqui isso
 * importa mais do que parece — a migração usa marcadores `x…` para as pessoas
 * sem CPF, e mascará-los produziria um número que parece documento.
 */
export function formatarCpf(cpf: string): string {
  const d = cpf.replace(/\D/g, '');
  if (d.length !== 11) return cpf;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

/**
 * Inscrição estadual do Paraná: oito dígitos, hífen, mais dois.
 *
 * É como aparece no cartão do contribuinte. O SPED guarda corrido, e conferir
 * `9072516408` contra o documento exige contar dígito a dígito. Outros
 * comprimentos saem como vieram — a máscara é do formato paranaense.
 */
export function formatarCadIcms(valor: string | null | undefined): string {
  if (typeof valor !== 'string') return '';
  const numeros = valor.replace(/\D/g, '');

  return numeros.length === 10 ? numeros.replace(/^(\d{8})(\d{2})$/, '$1-$2') : valor;
}

/**
 * Máscara de moeda enquanto se digita — o `R$ 0,00` de todo campo de valor.
 *
 * Os dígitos entram pela direita, em centavos: `1` → `0,01`, `100` → `1,00`,
 * `100000` → `1.000,00`. É o comportamento dos campos de valor do Deca 1 e
 * dos apps bancários, e o que impede o "1000000" cru que ninguém sabe se é
 * um milhão ou dez mil. Apagar tudo devolve vazio, não `0,00` — vazio é
 * "não informado", e os formulários tratam os dois de forma diferente.
 *
 * O texto produzido é o que `lerValorDigitado` lê de volta.
 */
export function mascararMoeda(texto: string): string {
  const digitos = texto.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
  if (digitos === '') return '';
  const centavos = Number(digitos);
  if (!Number.isFinite(centavos)) return '';
  return (centavos / 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Um número gravado → o texto do campo mascarado (`1234.5` → `1.234,50`). */
export function moedaParaCampo(valor: number | null | undefined): string {
  if (valor === null || valor === undefined || !Number.isFinite(valor)) return '';
  return valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
